import type { Model } from 'survey-core';

// The answer ceilings ktech-forms-api enforces on a submitted form (src/services/DigitalForms/responseLimits.ts
// there; KOI-1338). They sit far past anything a person filling in a form types, and exist to stop crafted
// requests. They are mirrored here so a person who somehow reaches one is told on the field, before submitting,
// rather than refused by the server after. Keep the two in step.
export const RESPONSE_LIMITS = {
  answerChars: 20_000,
  signatureChars: 1_000_000,
  emojiPerAnswer: 10,
  emojiPerSubmission: 100,
} as const;

const SIGNATURE_PREFIX = 'data:image/';

export const ANSWER_TOO_LONG_MESSAGE = `This answer is too long. Please keep it under ${RESPONSE_LIMITS.answerChars.toLocaleString('en-US')} characters.`;
export const TOO_MANY_EMOJI_MESSAGE = `Please use no more than ${RESPONSE_LIMITS.emojiPerAnswer} emoji in one answer.`;
export const TOO_MANY_EMOJI_IN_FORM_MESSAGE = `This form has more than ${RESPONSE_LIMITS.emojiPerSubmission} emoji in total. Please remove some.`;

// An emoji is a character shown as an emoji by default, or one that U+FE0F asks to be shown as one. Symbols
// that have a text form (™ © ☐ ✓ ☹) are not counted unless so asked.
const EMOJI_VARIATION_SELECTOR = String.fromCharCode(0xfe0f);
const isEmoji = (grapheme: string): boolean => /\p{Emoji_Presentation}/u.test(grapheme) || grapheme.includes(EMOJI_VARIATION_SELECTOR);
const graphemes = new Intl.Segmenter('en', { granularity: 'grapheme' });

/** Emoji in a piece of text, counting a joined sequence (skin tone, family, flag, keycap) once. */
export const countEmoji = (text: string): number => {
  if (!isEmoji(text)) return 0;
  let count = 0;
  for (const { segment } of graphemes.segment(text)) if (isEmoji(segment)) count += 1;
  return count;
};

/**
 * The message for a question whose value breaks a ceiling, or null. Every text in the value is checked, at any
 * depth (multiple text, matrix, dynamic panel).
 */
export const answerLimitMessage = (value: unknown): string | null => {
  const pending: unknown[] = [value];
  while (pending.length > 0) {
    const next = pending.pop();
    if (typeof next === 'string') {
      const isSignature = next.startsWith(SIGNATURE_PREFIX);
      if (next.length > (isSignature ? RESPONSE_LIMITS.signatureChars : RESPONSE_LIMITS.answerChars)) return ANSWER_TOO_LONG_MESSAGE;
      if (!isSignature && countEmoji(next) > RESPONSE_LIMITS.emojiPerAnswer) return TOO_MANY_EMOJI_MESSAGE;
    } else if (next !== null && typeof next === 'object') {
      pending.push(...Object.values(next));
    }
  }
  return null;
};

/** Emoji in every text of a value, at any depth; signatures hold none. */
const countAllEmoji = (value: unknown): number => {
  let total = 0;
  const pending: unknown[] = [value];
  while (pending.length > 0) {
    const next = pending.pop();
    if (typeof next === 'string') total += next.startsWith(SIGNATURE_PREFIX) ? 0 : countEmoji(next);
    else if (next !== null && typeof next === 'object') pending.push(...Object.values(next));
  }
  return total;
};

/**
 * Validates every answer against the ceilings. Over the per-form emoji total, every answer holding emoji is
 * marked, so the person can see where to trim. SurveyJS's own maxTextLength is not used: it puts a
 * "0/20000" counter under every text box.
 */
export const applyResponseLimits = (survey: Model): void => {
  survey.onValidateQuestion.add((_, options) => {
    const message = answerLimitMessage(options.value);
    if (message) options.error = message;
    else if (countAllEmoji(options.value) > 0 && countAllEmoji(survey.data) > RESPONSE_LIMITS.emojiPerSubmission) {
      options.error = TOO_MANY_EMOJI_IN_FORM_MESSAGE;
    }
  });
};
